import React, { useState } from "react";
import Modal from "@components/common/Modal/Modal";
import type { CommitteeDetailsModalProps, CommitteeMember } from "../types/committeeDetails";
import type { CommitteeDetailsResponse } from "../MyCommittee/services/committeeDetails.service";
import InfiniteScroll from "@components/common/InfiniteScroll/InfiniteScroll";
import { MemberCard } from "@components/common/MemberCard";
import { staticCommitteeData } from "./data/mockdata";
import Spinner from "@components/common/Spinner/Spinner";
import { useGetCommitteeDetails } from "../History/services/queries";
import { mapCommitteeMemberToCardData, type ApiCommitteeMember } from "../utils/committeeMemberMapper";

const CommitteeDetailsModal: React.FC<CommitteeDetailsModalProps> = ({ isOpen, onClose, committeeData }) => {
	const [hasNextPage, setHasNextPage] = useState(true);
	const [isFetching, setIsFetching] = useState(false);

	const getCommitteeId = (): string => {
		if (committeeData && "commitId" in committeeData) {
			return committeeData.commitId as string;
		}
		if (committeeData && "uuid" in committeeData) {
			return committeeData.uuid as string;
		}
		return "";
	};

	const { data: apiResponse, isLoading: isApiLoading } = useGetCommitteeDetails(getCommitteeId());

	const isApiData = (data: unknown): data is CommitteeDetailsResponse => {
		if (!data || typeof data !== "object" || data === null) {
			return false;
		}

		const dataObj = data as Record<string, unknown>;
		const hasApplicationCommitteeMembers = "applicationCommitteeMembers" in dataObj;
		const hasData = "data" in dataObj;
		return hasApplicationCommitteeMembers || hasData;
	};

	const apiData = isApiData(apiResponse) ? apiResponse : null;
	const staticData = !isApiData(apiResponse) ? committeeData : null;
	const data = apiData || staticData || staticCommitteeData;

	const fetchNextPage = () => {
		if (isFetching || !hasNextPage) return;

		setIsFetching(true);
		setTimeout(() => {
			setHasNextPage(false);
			setIsFetching(false);
		}, 500);
	};

	const getApplicationId = (): string => {
		if (committeeData && isApiData(committeeData)) {
			return committeeData.data?.application?.uuid || "N/A";
		}
		if (data && "applicationId" in data) {
			return (data as { applicationId: string }).applicationId || "N/A";
		}
		if (committeeData && "appId" in committeeData) {
			return (committeeData as { appId: string }).appId || "N/A";
		}
		return "N/A";
	};

	const getInstituteName = (): string => {
		if (committeeData && isApiData(committeeData)) {
			return committeeData.data?.application?.institute?.name || "N/A";
		}
		if (data && "instituteName" in data) {
			return (data as { instituteName: string }).instituteName || "N/A";
		}
		if (committeeData && "instName" in committeeData) {
			return (committeeData as { instName: string }).instName || "N/A";
		}
		return "N/A";
	};

	const getMembers = (): CommitteeMember[] => {
		if (apiResponse && isApiData(apiResponse)) {
			const apiResponseObj = apiResponse as unknown as Record<string, unknown>;
			const apiMembers =
				((apiResponseObj.applicationCommitteeMembers ||
					(apiResponseObj.data as Record<string, unknown>)
						?.applicationCommitteeMembers) as unknown as CommitteeMember[]) || [];
			return apiMembers;
		}

		const staticMembers = (staticCommitteeData.members as unknown as CommitteeMember[]) || [];
		return staticMembers;
	};

	const isLoading = isApiLoading;

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={`Application - ${getApplicationId()}`}
			width="max-w-4xl"
			height="max-h-[98vh]"
			className="overflow-hidden">
			<div className="space-y-4">
				<div className="pb-4">
					<div className="text-lg font-semibold text-gray-900">Institute Name: {getInstituteName()}</div>
				</div>

				{isLoading ? (
					<div className="flex justify-center items-center py-8">
						<Spinner />
					</div>
				) : (
					<InfiniteScroll
						items={getMembers()}
						renderItem={(member: unknown) => {
							const apiMember = member as ApiCommitteeMember;
							return (
								<MemberCard
									key={apiMember?.id}
									member={mapCommitteeMemberToCardData(apiMember)}
									showAssignedLabs
								/>
							);
						}}
						isLoading={false}
						hasNextPage={hasNextPage}
						fetchNextPage={fetchNextPage}
						isFetching={isFetching}
						noDataMessage={"No members found"}
						className="space-y-4 max-h-[70vh] overflow-y-auto pr-2"
						scrollRootId="committee-members-scroll"
					/>
				)}
			</div>
		</Modal>
	);
};

export default CommitteeDetailsModal;
